import { ref, computed, onMounted } from 'vue';
import store from '@/store';
import { useToast } from '@/composables/common/useToast';
import { useQueryList as useListQuery } from '@/composables/common/useQueryList';
import { camposPersonalizadosService } from '@/services/admin/bases/camposPersonalizados';

const VALID_CONTEXTS = ['bases', 'eventos', 'abonos'];

export function useCamposPersonalizados(context, entityId) {
    if (!VALID_CONTEXTS.includes(context)) {
        throw new Error(`Contexto inválido: "${context}". Valores permitidos: ${VALID_CONTEXTS.join(', ')}`);
    }

    const toast = useToast();

    const list = useListQuery({
        fetcher: async params => {
            try {
                const res = await camposPersonalizadosService.getList(
                    context, entityId, params.filters
                );
                return res.data || { data: [], meta: {} };
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error al obtener campos');
                throw error;
            }
        },
        listaIdSource: 'none',
        autoFetch: false,
    });

    const availableFields = ref([]);
    const availableFieldsLoading = ref(false);

    const isBases = computed(() => context === 'bases');

    const columns = computed(() => {
        if (isBases.value) {
            return [
                { key: 'name', label: 'Nombre' },
                { key: 'type', label: 'Tipo' },
                { key: 'required', label: 'Requerido', centered:true },
                { key: 'deportista', label: 'Deportista', centered:true },
                { key: 'representante', label: 'Representantes', class: 'tw:w-36', centered:true },
                { key: 'opciones', label: 'Opciones', class: 'tw:w-32', centered:true }
            ];
        }
        return [
            { key: 'name', label: 'Nombre' },
            { key: 'type', label: 'Tipo' },
            { key: 'required', label: 'Requerido' },
            { key: 'opciones', label: 'Opciones' }
        ];
    });

    async function addFields(selectedFields) {
        list.loading.value = true;
        try {
            const res = await camposPersonalizadosService.add(context, entityId, selectedFields);
            toast.success(res.data?.message || 'Campos añadidos correctamente');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al añadir campos');
        }
        await list.fetch();
    }

    async function deleteField(fieldId) {
        list.loading.value = true;
        try {
            const res = await camposPersonalizadosService.delete(context, entityId, fieldId, list.filters.value);
            toast.success(res.data?.message || 'Campo eliminado correctamente');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al eliminar campo');
        }
        await list.fetch();
    }

    async function fetchAvailableFields() {
        availableFieldsLoading.value = true;
        try {
            const teatro = store.getters.getTeatro;
            const res = await camposPersonalizadosService.getAvailableFields(teatro.id);
            availableFields.value = res.data?.data || [];
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al cargar campos disponibles');
        } finally {
            availableFieldsLoading.value = false;
        }
    }

    onMounted(() => {
        list.fetch();
        fetchAvailableFields();
    });

    return {
        loading: list.loading,
        fields: computed(() => list.data.value.data || []),
        meta: list.meta,
        filters: list.filters,
        columns,
        isBases,
        availableFields,
        availableFieldsLoading,
        addFields,
        deleteField,
        fetchAvailableFields,
    };
}
